import { useMutation } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { useContext, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Typography } from 'design-system/src/components/Typography/Typography';
import { InfoBox } from 'design-system/src/components/InfoBox/InfoBox';
import { StandardErrorToast, Toast } from 'design-system/src/components/Toast/Toast';
import Button from 'design-system/src/components/Button/Button';
import ButtonRow, { Alignment } from 'design-system/src/components/ButtonRow/ButtonRow';
import { RadioLabel, RadioOptionWrapper, RadioBlockGroup } from 'design-system/src/components/Radio/Radio';
import {
  client,
  getRxFilePreviewUrl,
  NewCommentFile,
  FormFilesDocumentTypes,
  TaskComment,
  TaskEvent,
  OutboundTaskTypeKey,
  OutboundTaskType,
} from '../../../../api';
import type { Task, WithAdditionalTaskResponseFields } from '../../types';
import { TaskContext } from '../../contexts';
import { RenderHtmlString } from 'design-system/src/components/RenderHtmlString/RenderHtmlString';
import DeclineModal from '../../DeclineModal';
import AppealRequestModal, { AdditionalInput, AppealRequestModalType } from '../../AppealRequestModal';
import SubmitAppealModal from '../../SubmitAppealModal';
import AppealEditModal from '../../AppealEditModal';
import NewFollowupTaskModal from '../../NewFollowupTaskModal';
import { TaskInstructionSummary, TaskCompletionEventBanner } from '../../shared';
import { OtherRequestButton } from '../../actions/shared/OtherRequestButton';
import { getAppealActionLabel, type CashPayMetadata } from '../../actions/appealReviewActionHelpers';
import { Step, updatedStepsOnSelect, isOptionChecked } from '../../actions/shared/helpers';
import {
  FormActionContainer,
  StyledIframe,
  StickyButtonContainer,
  FLOATING_BUTTON_TOP_OFFSET,
  FLOATING_BUTTON_RIGHT_OFFSET,
} from '../../actions/shared/styles';
import { FloatingEditButton } from '../../../FloatingEditButton/FloatingEditButton';
import { EmbeddedForm } from '../../../EmbeddedForm';
import { validateEmbeddedFormInfo } from '../../../../lib/embeddedFormInfo';
import { AttachedFile } from '../../Files';
import { sponsoredTaskCopyWithAppealGenerationNotice } from '../../../businessRelationshipNotices/copy';
import { getBenefitTypeLabel } from '../../utils';
import { formatDrugName } from 'shared-utils/utils/dropdownFilter';
import { PharmaPilotDrug } from '../../../../types';
import styled from 'styled-components';
import { StructuredTaskViewWrapper } from '../StructuredTaskViewWrapper';

const InstructionsWrapper = styled.div`
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const StyledFormActionContainer = styled(FormActionContainer)`
  padding-bottom: 0;
`;

type ModalState = 'decline' | 'initiate' | 'submit_appeal' | 'edit_appeal' | 'other_request';

/**
 * Dedicated task view for PA denied - Ready to appeal (PA_DENIAL_NEXT_STEPS_AND_APPEAL_REVIEW).
 */
const PaDenialNextStepsAndAppealReviewTaskView: React.FC<{
  taskResponse: WithAdditionalTaskResponseFields<Task>;
}> = ({ taskResponse }) => {
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [modalState, setModalState] = useState<{ show: boolean; type: ModalState }>({
    show: false,
    type: 'decline',
  });
  const [documentId, setDocumentId] = useState<string | null>(taskResponse.task.document_id ?? null);
  const [newFilesUploaded, setNewFilesUploaded] = useState<NewCommentFile[]>([]);
  const [additionalInput, setAdditionalInput] = useState<AdditionalInput>({
    needs_draft_review: false,
  });
  const [inputValue, setInputValue] = useState<string>('');

  const { taskId, onTaskUpdate } = useContext(TaskContext);
  const navigate = useNavigate();
  const { drug, patient, prescriber } = taskResponse;
  const task = taskResponse.task;
  const isTrainingMode = import.meta.env.VITE_TRAINING_MODE === 'true';

  const formInfo = validateEmbeddedFormInfo(task);
  const cashPayMetadata: CashPayMetadata = {
    is_appeal_after_discount_price_selection: task.is_appeal_after_discount_price_selection ?? false,
    is_cash_price_consent_requested: task.is_cash_price_consent_requested ?? false,
    is_cash_price_not_affordable: task.is_cash_price_not_affordable ?? false,
  };
  const pilotDrugName = task.pilot_drug_name ?? undefined;

  const [nextSteps, setNextSteps] = useState<Step[]>([
    {
      label: getAppealActionLabel('Approve appeal letter', pilotDrugName, cashPayMetadata),
      value: 'approve',
      checked: true,
    },
    {
      label: getAppealActionLabel('Request appeal letter edits', pilotDrugName, cashPayMetadata),
      value: 'request_changes',
      checked: false,
    },
  ]);

  const closeModal = () => setModalState((prev) => ({ ...prev, show: false }));

  const initiate = useMutation({
    mutationFn: async () => {
      if (isTrainingMode) {
        console.warn(`Training Mode: Mocking task response "initiate" for task ID: ${taskId}`);
        return;
      }

      const formData = new FormData();
      formData.append('task_response', 'initiate');
      formData.append('message', inputRef.current?.value || '');
      formData.append('additional_input', JSON.stringify(additionalInput));

      if (newFilesUploaded.length) {
        const formFilesDocumentTypes: FormFilesDocumentTypes = {};
        newFilesUploaded.forEach((fileInfo) => {
          formData.append('files[]', fileInfo.file);
          formFilesDocumentTypes[fileInfo.file.name] = fileInfo.document_type;
        });
        formData.append('form_files_document_types', JSON.stringify(formFilesDocumentTypes));
      }

      await client.post(`tasks/${taskId}/respond`, formData);
    },
    onSuccess: () => {
      onTaskUpdate();
      toast.custom(() => <Toast variant="success">Task initiated.</Toast>);
      navigate('/tasks');
    },
    onError: (error) => {
      toast.custom(() => <StandardErrorToast />);
      console.error(error);
      Sentry.captureException(error);
    },
  });

  const extraData: { label: string; value: string }[] = [];
  const benefitTypeLabel = getBenefitTypeLabel(task);
  if (benefitTypeLabel) {
    extraData.push({ label: 'Benefit type', value: benefitTypeLabel });
  }

  const isCompleted = task.status === 'completed';

  const initialComment =
    task.timeline && task.timeline.length > 0 && task.timeline[0].item_type === 'comment'
      ? (task.timeline[0] as TaskComment)
      : undefined;

  // Get completion event for completed tasks
  let completionEvent: TaskEvent | undefined;
  if (isCompleted) {
    if (task.timeline && task.timeline.length > 1) {
      const lastTimelineItem = task.timeline[task.timeline.length - 1];
      if (lastTimelineItem?.item_type === 'event') {
        completionEvent = lastTimelineItem as TaskEvent;
      }
    }
    if (!completionEvent) {
      Sentry.captureMessage('No completion event found for PA Denial Next Steps and Appeal Review task', {
        level: 'error',
        extra: {
          taskId,
        },
      });
    }
  }

  const showBusinessRelationshipNotice = task.is_pharma_contract_task ?? true;
  const businessRelationshipNoticeCopy = sponsoredTaskCopyWithAppealGenerationNotice;

  const showModalForSelection = () => {
    if (isOptionChecked(nextSteps, 'approve')) {
      setModalState({ show: true, type: 'submit_appeal' });
    } else {
      // request_changes
      setModalState({ show: true, type: 'initiate' });
    }
  };

  let modalToShow = null;
  if (modalState.show) {
    switch (modalState.type) {
      case 'decline':
        modalToShow = (
          <DeclineModal
            closeModal={closeModal}
            patient={patient}
            drug={drug}
            taskKey={task.task_key as OutboundTaskTypeKey}
            taskId={taskId}
            onTaskUpdate={onTaskUpdate}
          />
        );
        break;
      case 'initiate':
        modalToShow = (
          <AppealRequestModal
            closeModal={closeModal}
            initiate={initiate}
            inputRef={inputRef}
            patient={patient}
            drug={drug}
            task={task}
            inputValue={inputValue}
            setInputValue={setInputValue}
            files={newFilesUploaded}
            onFilesChange={setNewFilesUploaded}
            setAdditionalInput={setAdditionalInput}
            additionalInput={additionalInput}
            appealRequestModalType={'revise_with_next_steps' as AppealRequestModalType}
          />
        );
        break;
      case 'submit_appeal':
        modalToShow = (
          <SubmitAppealModal
            closeModal={closeModal}
            patient={patient}
            drug={drug}
            taskId={taskId}
            includeEnrollmentNotice={true}
          />
        );
        break;
      case 'edit_appeal':
        modalToShow = documentId ? (
          <AppealEditModal
            closeModal={closeModal}
            patient={patient}
            drug={drug}
            documentId={documentId}
            setDocumentId={setDocumentId}
            taskId={taskId}
          />
        ) : null;
        break;
      case 'other_request':
        modalToShow = (
          <NewFollowupTaskModal
            closeModal={closeModal}
            patient={patient}
            drug={drug}
            patientId={patient.id}
            prescriptionId={drug.id}
            taskId={taskId}
            relatedTaskKey={task.task_key as OutboundTaskTypeKey}
            relatedTaskType={task.type as OutboundTaskType}
          />
        );
        break;
    }
  }

  return (
    <>
      <StructuredTaskViewWrapper
        taskType={(task.task_title || task.type) as OutboundTaskType}
        generalInfoTableProps={{
          patientName: drug.patient_name,
          prescriberName: drug.prescriber_name,
          dob: drug.dob,
          prescribedOn: drug.prescription_recieved_at,
          drugName: drug.name,
          prescriptionId: drug.id,
          extraData,
        }}
        taskCreatedAt={task.created_at}
        isCompleted={isCompleted}
        showBusinessRelationshipSmallPrint
        sidebar={{
          drug: {
            data: drug,
            showBusinessRelationshipNotice,
            businessRelationshipNoticeCopy,
          },
          patient: patient ?? undefined,
          prescriber: prescriber ?? undefined,
        }}
      >
        {isCompleted && <TaskCompletionEventBanner completionEvent={completionEvent} />}

        <InstructionsWrapper>
          <TaskInstructionSummary>
            Please review the generated appeal letter and select an option below:
          </TaskInstructionSummary>
          {initialComment && (
            <>
              <Typography styledAs="bodyExtraSmallDMSans">
                <RenderHtmlString>{initialComment.message}</RenderHtmlString>
              </Typography>
              {initialComment.attachment_document_ids && initialComment.attachment_document_ids.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {initialComment.attachment_document_ids.map((fileId) => (
                    <AttachedFile key={fileId} fileId={fileId} />
                  ))}
                </div>
              )}
            </>
          )}
          {task.pilot_drug_name === PharmaPilotDrug.TREMFYA && (
            <InfoBox variant="success" noTopMargin noBottomMargin>
              <Typography styledAs="bodySmallDMSans">
                Most appeals for {formatDrugName(task.pilot_drug_name)} are successful in overturning denials, based on
                Tandem data.
              </Typography>
            </InfoBox>
          )}
        </InstructionsWrapper>

        {!isCompleted && (documentId || formInfo) && (
          <StyledFormActionContainer>
            {formInfo ? (
              <EmbeddedForm formInfo={formInfo} />
            ) : (
              documentId && <StyledIframe src={getRxFilePreviewUrl(drug.id, documentId)} />
            )}
            <StickyButtonContainer>
              {documentId && (
                <FloatingEditButton
                  top={FLOATING_BUTTON_TOP_OFFSET}
                  right={FLOATING_BUTTON_RIGHT_OFFSET}
                  onClick={() => setModalState({ show: true, type: 'edit_appeal' })}
                />
              )}
              {task.pilot_drug_name === PharmaPilotDrug.TALTZ && (
                <InfoBox variant="info" noTopMargin noBottomMargin>
                  <Typography styledAs="bodySmallDMSans">
                    Support from manufacturer program to access Taltz typically requires submission of an appeal
                  </Typography>
                </InfoBox>
              )}
              <RadioBlockGroup $variant="outlined">
                {nextSteps.map((step) => (
                  <RadioOptionWrapper
                    id={step.value}
                    name="next-steps"
                    value={step.value}
                    checked={step.checked}
                    onChange={() => setNextSteps(updatedStepsOnSelect(nextSteps, step))}
                    key={step.value}
                    variant="outlined"
                    disabled={initiate.isPending}
                  >
                    <RadioLabel>{step.label}</RadioLabel>
                  </RadioOptionWrapper>
                ))}
              </RadioBlockGroup>
              <ButtonRow align={Alignment.SpaceBetween}>
                <ButtonRow>
                  <Button
                    variant="tertiary"
                    size="sm"
                    onClick={() => setModalState({ show: true, type: 'decline' })}
                    disabled={initiate.isPending}
                  >
                    Cancel prescription
                  </Button>
                  <OtherRequestButton
                    setShowNewFollowupTaskModal={(show) => setModalState({ show, type: 'other_request' })}
                  />
                </ButtonRow>
                <Button size="sm" onClick={() => showModalForSelection()} isLoading={initiate.isPending}>
                  Submit
                </Button>
              </ButtonRow>
            </StickyButtonContainer>
          </StyledFormActionContainer>
        )}
      </StructuredTaskViewWrapper>

      {modalState.show && modalToShow}
    </>
  );
};

export default PaDenialNextStepsAndAppealReviewTaskView;
